// frontend/src/pages/ChartOfAccounts/ChartOfAccounts.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AccountFormModal from "./AccountFormModal";
import { coaApi } from "./coaApi";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./ChartOfAccounts.css";

const MAIN_TYPES = [
  "All",
  "Asset",
  "Liability",
  "Equity",
  "Income",
  "Expense",
];

const canonicalMainType = (value) => {
  if (value === "Capital") return "Equity";
  if (value === "Revenue") return "Income";
  return value || "";
};

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id || value.id || "";
  return value;
};

const getType = (account) => {
  if (account.accountingType) {
    return account.accountingType;
  }

  if (
    account.accounting_type &&
    typeof account.accounting_type === "object"
  ) {
    return account.accounting_type;
  }

  return {};
};

const flattenTree = (nodes, expanded, depth = 0, result = []) => {
  for (const node of nodes || []) {
    result.push({ account: node, depth });

    if (expanded.has(String(node._id))) {
      flattenTree(node.children || [], expanded, depth + 1, result);
    }
  }

  return result;
};

export default function ChartOfAccounts() {
  const navigate = useNavigate();
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency } = useCurrency();
  const isSystemAdmin =
    userType === "system" &&
    ["developer", "admin"].includes(currentUser?.role);
  const contextCompanyId =
    userType === "company"
      ? getId(company) || getId(currentUser?.company_id)
      : "";

  // ✅ Compute company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(contextCompanyId);
  const targetCompanyId = isSystemAdmin
    ? selectedCompanyId
    : contextCompanyId;
  const [tree, setTree] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    posting: 0,
    control: 0,
    protected: 0,
    by_main_type: {},
  });
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState("");
  const [mainType, setMainType] = useState("All");
  const [status, setStatus] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [busyId, setBusyId] = useState("");

  // ✅ Format account balance using the account's own currency first,
  // falling back to the company currency.
  const getBalanceDisplay = useCallback(
    (account) => {
      const signedBalance =
        Number(account.consolidated_balance) ||
        Number(account.signed_balance) ||
        (Number(account.opening_debit || 0) +
          Number(account.debit_total || 0) -
          Number(account.opening_credit || 0) -
          Number(account.credit_total || 0));

      const absoluteBalance = Math.abs(signedBalance);
      const side =
        signedBalance > 0
          ? "DR"
          : signedBalance < 0
            ? "CR"
            : "";

      // ⭐ Prefer the account's actual stored currency_code so the display
      // matches what the backend will validate.
      let currencyCode =
        account.currency_code ||
        account.currencyDetails?.currency_code ||
        companyCurrency;

      if (typeof currencyCode !== "string" || !/^[A-Z]{3}$/.test(currencyCode)) {
        currencyCode = companyCurrency;
      }

      const formatted = formatCurrency(absoluteBalance, currencyCode);

      return {
        amount: formatted,
        side,
        currencyCode,
      };
    },
    [companyCurrency, formatCurrency],
  );

  useEffect(() => {
    if (!isSystemAdmin) return;

    const loadCompanies = async () => {
      try {
        const response = await coaApi.getCompanies();
        const nextCompanies = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        setCompanies(nextCompanies);
        setSelectedCompanyId((current) => current || getId(nextCompanies[0]));
      } catch (requestError) {
        setError(requestError.message || "Failed to load companies");
        setLoading(false);
      }
    };

    loadCompanies();
  }, [isSystemAdmin]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      if (isSystemAdmin && !targetCompanyId) {
        setTree([]);
        setStats({ total: 0, active: 0, posting: 0, control: 0, protected: 0, by_main_type: {} });
        return;
      }

      const [treeResponse, statsResponse] = await Promise.all([
        coaApi.getTree({
          search,
          status,
          ...(targetCompanyId ? { company_id: targetCompanyId } : {}),
          ...(mainType !== "All" ? { main_type: mainType } : {}),
        }),
        coaApi.getStats({
          ...(targetCompanyId ? { company_id: targetCompanyId } : {}),
        }),
      ]);

      const nextTree = Array.isArray(treeResponse?.data)
        ? treeResponse.data
        : [];

      setTree(nextTree);
      setStats(statsResponse?.data || {});

      if (search.trim()) {
        const allVisibleIds = new Set();

        const collect = (nodes) => {
          for (const node of nodes || []) {
            allVisibleIds.add(String(node._id));
            collect(node.children || []);
          }
        };

        collect(nextTree);
        setExpanded(allVisibleIds);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [isSystemAdmin, targetCompanyId, mainType, search, status]);

  useEffect(() => {
    const timer = window.setTimeout(loadData, 250);
    return () => window.clearTimeout(timer);
  }, [loadData]);

  const rows = useMemo(
    () => flattenTree(tree, expanded),
    [tree, expanded],
  );

  const toggleExpanded = (id) => {
    setExpanded((current) => {
      const next = new Set(current);
      const key = String(id);

      if (next.has(key)) next.delete(key);
      else next.add(key);

      return next;
    });
  };

  const openCreate = () => {
    setEditingAccount(null);
    setModalOpen(true);
  };

  const openEdit = (account) => {
    setEditingAccount(account);
    setModalOpen(true);
  };

  const changeStatus = async (account) => {
    const nextStatus =
      account.status === "active" ? "inactive" : "active";

    if (
      !window.confirm(
        `${nextStatus === "inactive" ? "Deactivate" : "Activate"} ${account.account_code} — ${account.account_name}?`,
      )
    ) {
      return;
    }

    setBusyId(String(account._id));
    setError("");

    try {
      await coaApi.changeStatus(account._id, nextStatus);
      await loadData();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyId("");
    }
  };

  const removeAccount = async (account) => {
    if (
      !window.confirm(
        `Delete ${account.account_code} — ${account.account_name}? This is allowed only when the account has no children or transaction history.`,
      )
    ) {
      return;
    }

    setBusyId(String(account._id));
    setError("");

    try {
      await coaApi.remove(account._id);
      await loadData();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusyId("");
    }
  };

  // ⭐ UPDATED: force the protected accounts (and their descendants) to the
  // company currency so beginning-balance vouchers stop failing the
  // "All accounts must use the voucher currency" check.
  const repairSetup = async () => {
    const confirmed = window.confirm(
      `Install/repair the protected inventory accounts?\n\n` +
      `This will:\n` +
      `• archive unused legacy starter accounts,\n` +
      `• preserve any account with balances or history,\n` +
      `• force all protected accounts and their existing subaccounts ` +
      `to the company currency (${companyCurrency}).`
    );

    if (!confirmed) return;

    setLoading(true);
    setError("");

    try {
      await coaApi.ensureInventorySetup({
        ...(targetCompanyId ? { company_id: targetCompanyId } : {}),
        cleanup_legacy: true,
        force_currency_update: true,
        currency_code: companyCurrency,
      });
      await loadData();
    } catch (requestError) {
      setError(requestError.message);
      setLoading(false);
    }
  };

  const openLedger = (account) => {
    const accountId = getId(account);

    if (!accountId) {
      setError("Unable to open the ledger because the account ID is missing.");
      return;
    }

    navigate(`/account-ledger/${encodeURIComponent(accountId)}`);
  };

  return (
    <main className="coa-page">
      <header className="coa-page-header">
        <div>
          <p className="coa-eyebrow">Accounting</p>
          <h1>Chart of Accounts</h1>
          <p className="coa-subtitle">
            Protected inventory accounts with automatically numbered user subaccounts.
          </p>
        </div>

        <div className="coa-page-header__actions">
          <button
            type="button"
            className="coa-button coa-button--secondary"
            onClick={repairSetup}
            disabled={isSystemAdmin && !targetCompanyId}
          >
            Install or Repair standard setup
          </button>
          <button
            type="button"
            className="coa-button coa-button--primary"
            onClick={openCreate}
            disabled={isSystemAdmin && !targetCompanyId}
          >
            <span aria-hidden="true">＋</span>
            New subaccount
          </button>
        </div>
      </header>

      <section className="coa-stat-grid" aria-label="Account statistics">
        <article className="coa-stat-card">
          <span>Total accounts</span>
          <strong>{stats.total || 0}</strong>
          <small>{stats.active || 0} active</small>
        </article>
        <article className="coa-stat-card">
          <span>Posting accounts</span>
          <strong>{stats.posting || 0}</strong>
          <small>Available to transactions</small>
        </article>
        <article className="coa-stat-card">
          <span>Protected accounts</span>
          <strong>{stats.protected || 0}</strong>
          <small>Fixed IMS ledger accounts</small>
        </article>
        <article className="coa-stat-card coa-stat-card--accent">
          <span>Income statement</span>
          <strong>
            {(stats.by_main_type?.Income || stats.by_main_type?.Revenue || 0) +
              (stats.by_main_type?.Expense || 0)}
          </strong>
          <small>Income and expense accounts</small>
        </article>
      </section>

      <section className="coa-workspace">
        <div className="coa-toolbar">
          {isSystemAdmin && (
            <select
              value={selectedCompanyId}
              onChange={(event) => setSelectedCompanyId(event.target.value)}
              aria-label="Select company"
            >
              <option value="">Select company</option>
              {companies.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.company_name}
                  {item.company_code ? ` (${item.company_code})` : ""}
                </option>
              ))}
            </select>
          )}

          <div className="coa-search">
            <span aria-hidden="true">⌕</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search code, account name, or description"
              aria-label="Search Chart of Accounts"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          <select
            value={mainType}
            onChange={(event) => setMainType(event.target.value)}
            aria-label="Filter by main account type"
          >
            {MAIN_TYPES.map((type) => (
              <option key={type} value={type}>
                {type === "All" ? "All account types" : type}
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filter by account status"
          >
            <option value="active">Active accounts</option>
            <option value="inactive">Inactive accounts</option>
            <option value="all">All statuses</option>
          </select>

          <button
            type="button"
            className="coa-button coa-button--secondary"
            onClick={loadData}
          >
            Refresh
          </button>
        </div>

        {error && <div className="coa-alert coa-alert--error">{error}</div>}

        <div className="coa-table-wrap">
          <table className="coa-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Type</th>
                <th>Balance</th>
                <th className="coa-actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4">
                    <div className="coa-empty-state">
                      <div className="coa-spinner" />
                      <strong>Loading Chart of Accounts…</strong>
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="4">
                    <div className="coa-empty-state">
                      <strong>No accounts found</strong>
                      <span>
                        Change the filters or repair the standard inventory setup.
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map(({ account, depth }) => {
                  const type = getType(account);
                  const hasChildren = Boolean(
                    account.has_children || account.children?.length,
                  );
                  const isExpanded = expanded.has(String(account._id));
                  const balanceDisplay = getBalanceDisplay(account);
                  const isBusy = busyId === String(account._id);
                  const isProtected = Boolean(
                    account.is_system_account || account.is_locked,
                  );

                  return (
                    <tr
                      key={account._id}
                      className={
                        account.status === "inactive"
                          ? "coa-row--inactive"
                          : ""
                      }
                    >
                      <td>
                        <div
                          className="coa-account-cell"
                          style={{ "--coa-depth": depth }}
                        >
                          <button
                            type="button"
                            className={`coa-tree-toggle ${hasChildren
                              ? ""
                              : "coa-tree-toggle--empty"
                              }`}
                            onClick={() =>
                              hasChildren &&
                              toggleExpanded(account._id)
                            }
                            aria-label={
                              hasChildren
                                ? isExpanded
                                  ? "Collapse account"
                                  : "Expand account"
                                : "No child accounts"
                            }
                          >
                            {hasChildren
                              ? isExpanded
                                ? "▾"
                                : "▸"
                              : "·"}
                          </button>

                          <div>
                            <button
                              type="button"
                              className="coa-account-link"
                              onClick={() =>
                                hasChildren
                                  ? toggleExpanded(account._id)
                                  : openLedger(account)
                              }
                            >
                              <strong>{account.account_code}</strong>
                              <span>{account.account_name}</span>
                            </button>
                            {account.description && (
                              <small>{account.description}</small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="coa-type-cell">
                          <strong>
                            {canonicalMainType(type.type || account.type) || "—"}
                          </strong>
                          <span>{type.sub_type || "Unclassified"}</span>
                        </div>
                      </td>
                      <td className="coa-money-cell">
                        <strong>{balanceDisplay.amount}</strong>
                        {balanceDisplay.side && (
                          <span className="coa-balance-side">
                            {balanceDisplay.side}
                          </span>
                        )}
                        <small>
                          {balanceDisplay.currencyCode}
                        </small>
                      </td>
                      <td>
                        <div className="coa-row-actions">
                          <button
                            type="button"
                            onClick={() =>
                              hasChildren
                                ? toggleExpanded(account._id)
                                : openLedger(account)
                            }
                            title={
                              hasChildren
                                ? isExpanded
                                  ? "Collapse subaccounts"
                                  : "Expand subaccounts"
                                : "View ledger"
                            }
                          >
                            {hasChildren
                              ? isExpanded
                                ? "Collapse"
                                : "Expand"
                              : "Ledger"}
                          </button>
                          {isProtected ? (
                            <span className="coa-badge coa-badge--neutral">
                              Locked
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => openEdit(account)}
                                title="Edit subaccount"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => changeStatus(account)}
                                disabled={isBusy}
                                title={
                                  account.status === "active"
                                    ? "Deactivate subaccount"
                                    : "Activate subaccount"
                                }
                              >
                                {account.status === "active"
                                  ? "Disable"
                                  : "Enable"}
                              </button>
                              <button
                                type="button"
                                className="coa-danger-action"
                                onClick={() => removeAccount(account)}
                                disabled={isBusy}
                                title="Delete subaccount"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <AccountFormModal
        open={modalOpen}
        account={editingAccount}
        accountTree={tree}
        defaultCompanyId={targetCompanyId}
        defaultCurrencyCode={companyCurrency}
        onClose={() => {
          setModalOpen(false);
          setEditingAccount(null);
        }}
        onSaved={loadData}
      />
    </main>
  );
}